import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { ShellIndexRow, type ShellIndexRowProps } from '@/components/layout/shell/shell-index-row'

export type IndexSurfaceShellProps = {
  /** Optional title / address row above the filter row. */
  header?: ReactNode
  /** Title row rendered as ShellIndexRow slots, under `header`. */
  indexRow?: Pick<ShellIndexRowProps, 'leading' | 'title' | 'trailing' | 'testId'>
  /** Full-width filter row (ExtensionFilterBar). Sits above the peek. */
  filter?: ReactNode
  /** Cmd+F PanelSearchBar row. */
  search?: ReactNode
  /** List + peek split (PanelGroup). */
  children: ReactNode
  /** ListSurfaceStatusBar under the split. */
  footer?: ReactNode
  className?: string
  'data-testid'?: string
  /** Test ids for the shrink-0 chrome wrappers around header, filter, search, and footer. */
  slotTestIds?: {
    header?: string
    filter?: string
    search?: string
    footer?: string
  }
}

/**
 * Index chrome: header -> optional full-width filter -> Cmd+F -> list/peek split -> footer.
 * Folder listings put the filter row in the list column so it shares a line with the peek header.
 * The index-row bands are ShellIndexRow (`@/components/layout/shell`): pass `indexRow` for a
 * plain one, or a component that renders it (ExtensionFilterBar singleRow, FolderFilterRow,
 * PeekHeader row). Do not paste the row height here or in callers.
 */
export function IndexSurfaceShell({
  header,
  indexRow,
  filter,
  search,
  children,
  footer,
  className,
  'data-testid': dataTestId,
  slotTestIds,
}: IndexSurfaceShellProps) {
  return (
    <div
      className={cn('flex h-full min-h-0 w-full flex-col overflow-hidden', className)}
      data-testid={dataTestId}
    >
      {header ? (
        <div className="shrink-0" data-testid={slotTestIds?.header}>
          {header}
        </div>
      ) : null}
      {indexRow ? <ShellIndexRow className="bg-background" {...indexRow} /> : null}
      {filter ? (
        <div className="shrink-0" data-testid={slotTestIds?.filter}>
          {filter}
        </div>
      ) : null}
      {search ? (
        <div className="shrink-0" data-testid={slotTestIds?.search}>
          {search}
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
      {footer ? (
        <div className="shrink-0" data-testid={slotTestIds?.footer}>
          {footer}
        </div>
      ) : null}
    </div>
  )
}
